import { useEffect, useMemo, useState } from 'react'
import { Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom'

import { useAddresses } from '@/api/addresses/queries/useAddresses'
import { useCurrencyRates } from '@/api/currency/queries/useCurrencyRates'
import { useUserPkgs } from '@/api/user-package/queries/useUserPkgs'
import { PkgShippingMethod } from '@/api/user-package/types/UserPkg'
import { AppBreadcrumbs } from '@/Components/AppBreadcrumbs'
import { Spinner } from '@/Components/ui/spinner'
import { binarySearchClosestValue } from '@/helpers/binarySearchClosestNumber'
import calcWeight from '@/helpers/CalculationFunction'
import { getPrice } from '@/helpers/getPrice'
import { RoutePath } from '@/RoutePath'

import { Stepper } from '../ReceiptUpload/components/Stepper'
import { PaymentData } from './components/PaymentData'
import ProcessPage from './ProcessPage'
import ShippingPage, { ShippingOptions } from './ShippingPage'
import SummaryPage from './SummaryPage'

export default function PaymentPage() {
  const history = useHistory()
  const { data: userPkgs, isLoading } = useUserPkgs()
  const location = useLocation()
  const { pkgId } = useParams<{ pkgId: string }>()
  const pkg = userPkgs?.find(p => p.id === pkgId)
  const { data: currencyRates } = useCurrencyRates()
  const EURToUSD = currencyRates?.EURToUSD ?? 0
  const GBPToUSD = currencyRates?.GBPToUSD ?? 0
  const AEDToUSD = currencyRates?.AEDToUSD ?? 0
  const { data: addresses } = useAddresses()
  const [shippingOptions, setShippingOptions] = useState<ShippingOptions>({
    selectedAddressId: '',
    selectedShippingMethod: PkgShippingMethod.DoorToDoor,
    isInsuranceAdded: false,
  })
  const [paymentData, setPaymentData] = useState<PaymentData>({
    selectedPaymentMethod: 'creditCard',
    couponCode: '',
    coupon: null,
    feesDiscount: 0,
    israeliId: '',
    paymentUrl: '',
    totalPrice: 0,
  })

  const pkgValue = useMemo(() => {
    return pkg?.valueInUSD || (pkg?.price ? parseFloat(getPrice(pkg, EURToUSD, GBPToUSD, AEDToUSD)) : 0)
  }, [pkg, EURToUSD, GBPToUSD, AEDToUSD])

  const shippingPrices = useMemo(() => {
    if (!pkg || !pkgValue) return { [PkgShippingMethod.DoorToDoor]: 0, [PkgShippingMethod.PickUpPoint]: 0 }

    const weight = calcWeight(pkg.dimensions.width, pkg.dimensions.length, pkg.dimensions.height, pkg.dimensions.weight)
    return binarySearchClosestValue(weight, pkg.warehouse)
  }, [pkg, pkgValue])

  const insurancePrice = useMemo(() => {
    if (!pkg || !pkgValue) return 0
    const fixedRate = 1 // $
    const proportionalRate = 0.015 // %
    const shippingPrice = shippingPrices[PkgShippingMethod.DoorToDoor]
    return parseFloat(((pkgValue + shippingPrice) * proportionalRate + fixedRate).toFixed(2))
  }, [pkg, pkgValue, shippingPrices])

  useEffect(() => {
    if (addresses && addresses.length > 0 && !shippingOptions.selectedAddressId) {
      setShippingOptions(prev => ({ ...prev, selectedAddressId: addresses.find(addr => addr.isDefault)?.id || addresses[0].id }))
    }
  }, [addresses, shippingOptions.selectedAddressId])

  useEffect(() => {
    if (!isLoading && !pkg) {
      history.push(RoutePath.ClientPkg)
      return
    }
    if (pkg && (!pkg.buyingReciptKey || !pkg.price || !pkg.falseReceiptDate)) {
      console.log('redirecting to receipt upload', pkg)
      history.push(`${RoutePath.ReceiptUpload}/${pkgId}`)
      return
    }
    if (pkg && pkg.shippingMethod) {
      setShippingOptions(prev => ({ ...prev, selectedShippingMethod: pkg.shippingMethod as PkgShippingMethod }))
    }
  }, [pkg, history, isLoading, pkgId])

  const updateShippingOptions = (options: Partial<ShippingOptions>) => {
    setShippingOptions(prev => ({ ...prev, ...options }))
  }

  const updatePaymentData = (data: Partial<PaymentData>) => {
    setPaymentData(prev => ({ ...prev, ...data }))
  }

  const currentStep = useMemo(() => {
    if (location.pathname.includes(RoutePath.PaymentProcess)) {
      return 3
    }
    if (location.pathname.includes(RoutePath.PaymentSummary)) {
      return 2
    }
    if (location.pathname.includes(RoutePath.PaymentShipping)) {
      return 1
    }
    return 0
  }, [location.pathname])

  const breadcrumbSteps = useMemo(() => {
    const res = [
      { label: 'החבילות שלי', to: RoutePath.ClientPkg },
      { label: 'הצהרת חבילה', to: `${RoutePath.ReceiptUpload}/${pkgId}` },
    ]
    if (currentStep >= 1) {
      res.push({ label: 'בחירת משלוח', to: `${RoutePath.PaymentShipping}/${pkgId}` })
    }
    if (currentStep >= 2) {
      res.push({ label: 'סיכום ואישור תשלום', to: `${RoutePath.PaymentSummary}/${pkgId}` })
    }
    if (currentStep >= 3) {
      res.push({ label: 'תשלום', to: `${RoutePath.PaymentProcess}/${pkgId}` })
    }
    return res
  }, [currentStep, pkgId])

  return (
    <div className='mx-auto min-h-full max-w-5xl p-6' dir='rtl'>
      {/* Header */}
      <div className='mb-6'>
        <AppBreadcrumbs steps={breadcrumbSteps} />
      </div>
      <div className='mx-auto max-w-xl'>
        <div className='mb-6 flex justify-center'>
          <Stepper currentStep={currentStep} totalSteps={4} />
        </div>

        {/* Child Components */}
        {isLoading || !pkg ? (
          <Spinner className='m-auto size-10' />
        ) : (
          <Switch>
            <Route path={`${RoutePath.PaymentShipping}/:pkgId`} exact>
              <ShippingPage
                pkg={pkg}
                shippingOptions={shippingOptions}
                onUpdateShippingOptions={updateShippingOptions}
                shippingPrices={shippingPrices}
                insurancePrice={insurancePrice}
              />
            </Route>
            <Route path={`${RoutePath.PaymentSummary}/:pkgId`} exact>
              <SummaryPage
                pkg={pkg}
                insurancePrice={insurancePrice}
                shippingPrices={shippingPrices}
                pkgValue={pkgValue}
                shippingOptions={shippingOptions}
                paymentData={paymentData}
                onUpdatePaymentData={updatePaymentData}
              />
            </Route>
            <Route path={`${RoutePath.PaymentProcess}/:pkgId`} exact>
              <ProcessPage
                shippingOptions={shippingOptions}
                paymentUrl={paymentData.paymentUrl}
                pkgId={pkgId}
                totalPrice={paymentData.totalPrice}
              />
            </Route>
          </Switch>
        )}
      </div>
    </div>
  )
}
